import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  getMixologistAdvancedQuestions,
  submitMixologistAdvanced,
  reportMixologistCheatingStrike,
  logMixologistQuizEvent,
  type MxQuestion,
} from "@/lib/mixologist.functions";

export const Route = createFileRoute("/_authenticated/bartender/advanced-quiz")({
  head: () => ({ meta: [{ title: "Mixologist Advanced Assessment — The Hospitality Reserve" }] }),
  component: AdvancedQuiz,
});

const PER_Q_SECONDS = 20;

type Phase =
  | { kind: "loading" }
  | { kind: "locked"; until?: string }
  | { kind: "intro"; questions: MxQuestion[] }
  | { kind: "mcq" }
  | { kind: "essay" }
  | { kind: "terminated" }
  | {
      kind: "result";
      passed: boolean;
      correct: number;
      total: number;
      percent: number;
      lockout_until: string | null;
    };

function fmtDays(until: string): string {
  const ms = new Date(until).getTime() - Date.now();
  if (ms <= 0) return "soon";
  const days = Math.ceil(ms / 86_400_000);
  return `${days} day${days === 1 ? "" : "s"}`;
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function AdvancedQuiz() {
  const navigate = useNavigate();
  const loadQ = useServerFn(getMixologistAdvancedQuestions);
  const submitFn = useServerFn(submitMixologistAdvanced);
  const strikeFn = useServerFn(reportMixologistCheatingStrike);
  const logEvent = useServerFn(logMixologistQuizEvent);

  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [questions, setQuestions] = useState<MxQuestion[]>([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [timeLeft, setTimeLeft] = useState(PER_Q_SECONDS);
  const [strikes, setStrikes] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [essay, setEssay] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const strikesRef = useRef(0);
  strikesRef.current = strikes;
  const phaseKindRef = useRef(phase.kind);
  phaseKindRef.current = phase.kind;
  const eventsRef = useRef<Array<{ type: string; at: string; payload?: Record<string, unknown> }>>([]);
  const remainingRef = useRef<Record<string, number>>({});
  const timeLeftRef = useRef(PER_Q_SECONDS);
  timeLeftRef.current = timeLeft;

  const track = (type: string, payload?: Record<string, unknown>) => {
    const evt = { type, at: new Date().toISOString(), payload };
    eventsRef.current.push(evt);
    // Fire-and-forget — never block UI on logging.
    void logEvent({ data: { event_type: type, payload } }).catch(() => {});
  };

  // Per-question option permutation so position can't be memorised.
  const perm = useMemo(() => {
    const m: Record<string, number[]> = {};
    for (const q of questions) m[q.id] = shuffle(q.options.map((_, i) => i));
    return m;
  }, [questions]);

  useEffect(() => {
    (async () => {
      const r = await loadQ();
      if ("error" in r) {
        if (r.lockout_until) {
          setPhase({ kind: "locked", until: r.lockout_until });
          return;
        }
        toast.error(r.error);
        navigate({ to: "/bartender", replace: true });
        return;
      }
      setQuestions(r.questions);
      setPhase({ kind: "intro", questions: r.questions });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-question countdown.
  useEffect(() => {
    if (phase.kind !== "mcq") return;
    setTimeLeft(PER_Q_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          // time-out → record nothing (will score 0), advance.
          queueMicrotask(() => advance());
          return PER_Q_SECONDS;
        }
        return t - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind, idx]);

  // Anti-cheat: visibility change + right click + selection during mcq phase.
  useEffect(() => {
    const onVis = () => {
      if (phaseKindRef.current !== "mcq" && phaseKindRef.current !== "essay") return;
      track("visibility_" + document.visibilityState, {
        question_index: idx,
        time_left: timeLeftRef.current,
        phase: phaseKindRef.current,
      });
      if (phaseKindRef.current === "mcq" && document.visibilityState === "hidden") {
        void handleStrike();
      }
    };
    const onBlur = () => {
      if (phaseKindRef.current === "mcq" || phaseKindRef.current === "essay") {
        track("window_blur", { question_index: idx, phase: phaseKindRef.current });
      }
    };
    const onCtx = (e: MouseEvent) => {
      if (phaseKindRef.current === "mcq" || phaseKindRef.current === "essay") {
        e.preventDefault();
        track("contextmenu_blocked", { phase: phaseKindRef.current });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    document.addEventListener("contextmenu", onCtx);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("contextmenu", onCtx);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const handleStrike = async () => {
    const next = strikesRef.current + 1;
    setStrikes(next);
    track("strike", { strike_number: next, question_index: idx, time_left: timeLeftRef.current });
    try {
      await strikeFn();
    } catch {
      /* non-fatal */
    }
    if (next >= 2) {
      if (timerRef.current) clearInterval(timerRef.current);
      track("one_strike_fail_triggered", { question_index: idx });
      // Force-submit as cheated.
      setPhase({ kind: "terminated" });
      try {
        const r = await submitFn({
          data: {
            answers,
            bar_manager_answer:
              essay.trim().length >= 20
                ? essay.trim()
                : "[terminated by anti-cheat before essay submission]",
            cheating_terminated: true,
            time_remaining_per_question: remainingRef.current,
            events: eventsRef.current,
          },
        });
        if ("ok" in r) {
          setPhase({
            kind: "result",
            passed: false,
            correct: 0,
            total: r.total ?? 5,
            percent: 0,
            lockout_until: r.lockout_until ?? null,
          });
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Submission failed.");
      }
      return;
    }
    setShowWarning(true);
  };

  const advance = () => {
    setIdx((i) => {
      const q = questions[i];
      if (q) remainingRef.current[q.id] = timeLeftRef.current;
      if (i + 1 >= questions.length) {
        if (timerRef.current) clearInterval(timerRef.current);
        track("mcq_complete", { time_remaining_per_question: remainingRef.current });
        setPhase({ kind: "essay" });
        return i;
      }
      track("question_advance", { from_index: i, time_left: timeLeftRef.current });
      return i + 1;
    });
  };

  const pickAnswer = (origIdx: number) => {
    const q = questions[idx];
    setAnswers((a) => ({ ...a, [q.id]: origIdx }));
    track("answer_selected", { question_id: q.id, time_left: timeLeftRef.current });
    advance();
  };

  const onSubmitEssay = async () => {
    if (essay.trim().length < 20) {
      toast.error("Please write at least a sentence or two.");
      return;
    }
    setSubmitting(true);
    track("essay_submit_attempt", { length: essay.trim().length });
    try {
      const r = await submitFn({
        data: {
          answers,
          bar_manager_answer: essay.trim(),
          cheating_terminated: false,
          time_remaining_per_question: remainingRef.current,
          events: eventsRef.current,
        },
      });
      if ("error" in r) {
        if (r.lockout_until) {
          setPhase({ kind: "locked", until: r.lockout_until });
          return;
        }
        throw new Error(r.error);
      }
      setPhase({
        kind: "result",
        passed: r.passed,
        correct: r.correct,
        total: r.total,
        percent: r.percent,
        lockout_until: r.lockout_until,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (phase.kind === "loading") return <div className="min-h-dvh bg-background" aria-busy="true" />;

  if (phase.kind === "locked") {
    return (
      <Shell title="Advanced Assessment Locked">
        <div className="text-center max-w-md mx-auto">
          <p className="tracking-wide-luxury text-accent text-xs">30-day lockout</p>
          <span className="block mx-auto mt-5 h-px w-12 bg-accent/60" />
          <h1 className="mt-6 font-display text-3xl">Re-attempt available in {phase.until ? fmtDays(phase.until) : "30 days"}</h1>
          <p className="mt-4 text-sm text-muted-foreground">
            You remain active as a bartender — only the mixologist add-on is locked.
          </p>
          <Link
            to="/bartender"
            className="mt-8 inline-block bg-foreground text-background tracking-wide-luxury py-3 px-6"
          >
            Back to dashboard
          </Link>
        </div>
      </Shell>
    );
  }

  if (phase.kind === "intro") {
    return (
      <Shell title="Mixologist Advanced Assessment">
        <div className="max-w-lg mx-auto text-center">
          <p className="tracking-wide-luxury text-accent text-xs">5 spec questions · 1 essay</p>
          <span className="block mx-auto mt-5 h-px w-12 bg-accent/60" />
          <h1 className="mt-6 font-display text-3xl">Pass mark: 85%</h1>
          <ul className="mt-6 text-sm text-muted-foreground space-y-2 text-left list-disc pl-6">
            <li>20-second countdown per multiple-choice question.</li>
            <li>Leaving this tab once is a warning. A second time fails you instantly.</li>
            <li>Right-click and copy are disabled during the assessment.</li>
            <li>Fail and you stay onboarded as a bartender — only the mixologist add-on resets.</li>
          </ul>
          <Button
            onClick={() => setPhase({ kind: "mcq" })}
            className="mt-8 w-full rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury py-6"
          >
            Begin assessment
          </Button>
        </div>
      </Shell>
    );
  }

  if (phase.kind === "terminated") {
    return (
      <Shell title="Assessment terminated">
        <div className="text-center max-w-md mx-auto">
          <p className="tracking-wide-luxury text-destructive text-xs">Failed: cheating detected</p>
          <span className="block mx-auto mt-5 h-px w-12 bg-destructive/60" />
          <h1 className="mt-6 font-display text-3xl">Finalising your result…</h1>
        </div>
      </Shell>
    );
  }

  if (phase.kind === "result") {
    return (
      <Shell title={phase.passed ? "Passed" : "Not Passed"}>
        <div className="max-w-md mx-auto text-center">
          <p className="tracking-wide-luxury text-accent text-xs">
            {phase.passed ? "Awaiting Manual Review" : "Mixologist track locked"}
          </p>
          <span className="block mx-auto mt-5 h-px w-12 bg-accent/60" />
          <h1 className="mt-6 font-display text-3xl">
            {phase.correct}/{phase.total} ({phase.percent}%)
          </h1>
          <p className="mt-4 text-sm text-muted-foreground">
            {phase.passed
              ? "Your essay is now with our team for CV scrutiny. You can continue using the platform as a bartender while we review."
              : `You'll need 85% to be tagged as a Mixologist. You remain onboarded as a bartender. Try again in ${phase.lockout_until ? fmtDays(phase.lockout_until) : "30 days"}.`}
          </p>
          <Link
            to="/bartender/application"
            className="mt-8 inline-block bg-foreground text-background tracking-wide-luxury py-3 px-6"
          >
            Continue to CV upload →
          </Link>
        </div>
      </Shell>
    );
  }

  if (phase.kind === "essay") {
    return (
      <Shell title="Bar Manager Question">
        <div className="max-w-2xl mx-auto select-none" style={{ userSelect: "none" }}>
          <p className="tracking-wide-luxury text-accent text-xs">Final question · no timer</p>
          <h2 className="mt-3 font-display text-xl leading-snug">
            You&apos;ve got a 500 cap wedding and there&apos;s a list of pornstar martini, Long
            Island ice tea, negroni, old fashioned and whiskey sour. What one would you cut and why?
          </h2>
          <Textarea
            value={essay}
            onChange={(e) => setEssay(e.target.value)}
            rows={8}
            className="mt-5 rounded-none"
            placeholder="Explain your reasoning…"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Saved with your application for manual scrutiny.
          </p>
          <Button
            onClick={onSubmitEssay}
            disabled={submitting}
            className="mt-6 w-full rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury py-6"
          >
            {submitting ? "Submitting…" : "Submit assessment"}
          </Button>
        </div>
      </Shell>
    );
  }

  // mcq
  const q = questions[idx];
  const orderPerm = perm[q.id] ?? q.options.map((_, i) => i);
  const timerColor = timeLeft <= 5 ? "bg-destructive" : timeLeft <= 10 ? "bg-orange-500" : "bg-emerald-600";

  return (
    <div
      className="min-h-screen bg-background text-foreground flex flex-col select-none"
      style={{ userSelect: "none" }}
    >
      <header className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between text-xs tracking-wide-luxury">
          <span className="text-accent">Question {idx + 1} of {questions.length}</span>
          <span className={`text-white px-3 py-1 ${timerColor}`}>{timeLeft}s</span>
        </div>
        <div className="mt-2 h-[2px] bg-muted overflow-hidden">
          <div
            className={`h-full transition-all ${timerColor}`}
            style={{ width: `${(timeLeft / PER_Q_SECONDS) * 100}%` }}
          />
        </div>
      </header>
      <main className="flex-1 flex items-start justify-center px-5 py-10">
        <div className="w-full max-w-2xl">
          <div className="bg-white text-foreground p-6">
            <p className="font-display text-lg leading-snug">{q.question}</p>
            <div className="mt-5 space-y-2">
              {orderPerm.map((origIdx, displayIdx) => (
                <button
                  key={origIdx}
                  type="button"
                  onClick={() => pickAnswer(origIdx)}
                  className="w-full text-left border border-border p-4 text-sm hover:border-accent transition-colors"
                >
                  <span className="font-display text-accent mr-3">
                    {String.fromCharCode(65 + displayIdx)}
                  </span>
                  {q.options[origIdx]}
                </button>
              ))}
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground text-center">
            Picking an answer advances automatically. No going back.
          </p>
        </div>
      </main>

      {showWarning && (
        <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-sm flex items-center justify-center px-6">
          <div className="bg-white border-2 border-destructive p-6 max-w-md text-center">
            <p className="tracking-wide-luxury text-destructive text-xs">Warning · Strike 1 of 2</p>
            <h3 className="mt-3 font-display text-2xl">Stay on this tab</h3>
            <p className="mt-3 text-sm text-muted-foreground">
              Leaving this tab again will result in an instant fail and a 30-day lockout from the
              mixologist track.
            </p>
            <Button
              onClick={() => setShowWarning(false)}
              className="mt-5 w-full rounded-none bg-destructive text-white hover:bg-destructive/90 tracking-wide-luxury"
            >
              I understand
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-6 py-5 border-b border-border">
        <p className="tracking-wide-luxury text-accent text-xs">The Hospitality Reserve</p>
        <p className="font-display text-lg leading-tight">{title}</p>
      </header>
      <main className="flex-1 flex items-center justify-center px-5 py-12">{children}</main>
    </div>
  );
}
